import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Cpu, Plus, Check, X, Pencil, Archive, RotateCcw, Inbox, Network, ExternalLink,
} from 'lucide-react';
import {
  SITE_TECH_ROLES, SITE_TECH_ROLE_LABELS, SITE_TECH_ATTRIBUTE_SPECS,
  siteTechLabel,
} from '@techstack/core';
import apiClient from '../../lib/apiClient';

/*
 * Technology at a location — S7 of docs/AGENCY-SITE-LOCATIONS-ROADMAP.md.
 *
 * Vocabularies, attribute specs and the label function come from
 * @techstack/core. There is already a backend mirror of that contract
 * (services/sites/siteVocab.js) held in step by `npm run sites:vocab-check`;
 * a third hand-typed copy here would be one nobody checks, and the first
 * symptom would be the form offering a role the API refuses.
 *
 * Three things below are policy, not styling:
 *
 *  1. THE GAP IS THE HEADLINE. "12 devices recorded" sounds like knowledge.
 *     "12 devices across 2 of 38 sites" is the truth. No public source states
 *     an agency's switch count (roadmap hurdle #10), so coverage here will be
 *     thin for a long time and the panel says so on its face rather than
 *     letting a short list imply a small estate.
 *
 *  2. A CLAIM LOOKS LIKE A CLAIM. Every row carries where it came from, and a
 *     candidate an assistant proposed is visibly not part of the estate until
 *     somebody approves it. Retired rows stay visible and struck through —
 *     "they pulled the Fortinet out last spring" is displacement intelligence.
 *
 *  3. THE DEFAULT PROVENANCE IS "field". Tech facts come out of meetings, so
 *     the honest status for most rows is "somebody at the account said so",
 *     not a desk verification. The form asks who.
 */

const ROLE_OPTIONS = SITE_TECH_ROLES.map((r) => ({ value: r, label: SITE_TECH_ROLE_LABELS[r] }));

/** Attribute fields worth offering for a role. Advisory — the bag is open. */
function specsFor(role) {
  return SITE_TECH_ATTRIBUTE_SPECS.filter((s) => s.roles.includes(role));
}

function attrLine(attributes) {
  const entries = Object.entries(attributes || {});
  if (!entries.length) return null;
  const byKey = new Map(SITE_TECH_ATTRIBUTE_SPECS.map((s) => [s.key, s]));
  return entries.map(([k, v]) => {
    const spec = byKey.get(k);
    const label = spec ? spec.label : k;
    const value = Array.isArray(v) ? v.join(', ') : String(v);
    return `${label}: ${value}${spec?.unit ? ` ${spec.unit}` : ''}`;
  }).join(' · ');
}

/* ─────────────────────── add / edit ─────────────────────── */

function TechForm({ siteId, row, onDone, onCancel }) {
  const queryClient = useQueryClient();
  const [role, setRole] = useState(row?.role || 'edge_firewall');
  const [rawLabel, setRawLabel] = useState(row?.rawLabel || '');
  const [quantity, setQuantity] = useState(row?.quantity ?? '');
  const [notes, setNotes] = useState(row?.notes || '');
  const [attrs, setAttrs] = useState(() => {
    const seed = {};
    for (const [k, v] of Object.entries(row?.attributes || {})) {
      seed[k] = Array.isArray(v) ? v.join(', ') : String(v);
    }
    return seed;
  });
  // Boots on the ground is the normal case here, so it is the default.
  const [heardFrom, setHeardFrom] = useState('');
  const [basis, setBasis] = useState('');
  const [err, setErr] = useState(null);

  const specs = specsFor(role);

  const save = useMutation({
    mutationFn: () => {
      const attributes = {};
      for (const s of specs) {
        const v = attrs[s.key];
        if (v === undefined || String(v).trim() === '') continue;
        attributes[s.key] = v;
      }
      const body = {
        role,
        rawLabel: rawLabel.trim() || null,
        quantity: quantity === '' ? null : Number(quantity),
        attributes,
        notes: notes.trim() || null,
        basis: basis.trim() || undefined,
        // 'field' means somebody at the account said so, and it outranks a
        // desk verification. Only claimed when a person is actually named.
        provenanceStatus: heardFrom.trim() ? 'field' : 'verified',
        confirmedBy: heardFrom.trim() || undefined,
      };
      return row
        ? apiClient.patch(`/api/sites/tech/${row.siteTechId}`, body)
        : apiClient.post(`/api/sites/${siteId}/tech`, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-detail', siteId] });
      queryClient.invalidateQueries({ queryKey: ['accounts-sites'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-tech-coverage'] });
      onDone();
    },
    // `e.data`, not `e.response.data` — apiClient is fetch, not axios.
    onError: (e) => setErr(e?.data?.error || e.message || 'Could not save'),
  });

  return (
    <form
      className="sites-form sites-tech-form"
      onSubmit={(e) => { e.preventDefault(); setErr(null); save.mutate(); }}
    >
      <div className="sites-form-grid">
        <label>
          What is it
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label>
          Model or name
          <input
            value={rawLabel} maxLength={200} placeholder="PA-3220, Catalyst 9300"
            onChange={(e) => setRawLabel(e.target.value)}
          />
        </label>
        <label>
          How many
          <input
            type="number" min="0" value={quantity} placeholder="leave blank if unknown"
            onChange={(e) => setQuantity(e.target.value)}
          />
        </label>
      </div>

      {specs.length > 0 && (
        <div className="sites-form-grid">
          {specs.map((s) => (
            <label key={s.key}>
              {s.label}{s.unit ? ` (${s.unit})` : ''}
              <input
                value={attrs[s.key] ?? ''} placeholder={s.hint || ''}
                onChange={(e) => setAttrs((a) => ({ ...a, [s.key]: e.target.value }))}
              />
            </label>
          ))}
        </div>
      )}

      <div className="sites-form-grid">
        <label>
          Who told you
          <input
            value={heardFrom} maxLength={200} placeholder="Rick at CDT — leave blank if you saw it yourself"
            onChange={(e) => setHeardFrom(e.target.value)}
          />
        </label>
        <label>
          How you know
          <input
            value={basis} maxLength={500} placeholder="8/14 QBR; network diagram in the 2025 TRP"
            onChange={(e) => setBasis(e.target.value)}
          />
        </label>
      </div>

      <label className="sites-form-wide">
        Notes
        <input value={notes} maxLength={2000} onChange={(e) => setNotes(e.target.value)} />
      </label>

      <p className="sites-muted">
        {heardFrom.trim()
          ? 'Recorded as heard from a person at the account — the strongest provenance we have, and it outranks anything a machine writes.'
          : 'Recorded as verified by you. Naming who told you makes it a field claim instead, which is the honest status for most tech facts.'}
      </p>

      {err && <p className="accounts-error">{err}</p>}

      <div className="sites-form-actions">
        <button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : (row ? 'Save changes' : 'Add')}
        </button>
        <button type="button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/* ─────────────────────── one site's tech ─────────────────────── */

function TechRow({ row, siteId, onEdit }) {
  const queryClient = useQueryClient();
  const act = useMutation({
    mutationFn: (verb) => apiClient.post(`/api/sites/tech/${row.siteTechId}/${verb}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-detail', siteId] });
      queryClient.invalidateQueries({ queryKey: ['accounts-sites'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-tech-candidates'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-tech-coverage'] });
    },
  });

  const attrs = attrLine(row.attributes);
  const isCandidate = row.status === 'candidate';
  const isRetired = row.status === 'retired';

  return (
    <li className={`sites-tech-row${isRetired ? ' is-retired' : ''}${isCandidate ? ' is-candidate' : ''}`}>
      <div className="sites-tech-main">
        <span className="sites-tech-label">
          {siteTechLabel({ role: row.role, quantity: row.quantity, rawLabel: row.rawLabel })}
        </span>
        {isCandidate && (
          <span className="sites-tech-flag" title="Proposed by an assistant. Not part of the estate until you approve it.">
            proposed
          </span>
        )}
        {isRetired && <span className="sites-tech-flag">removed</span>}
        {attrs && <div className="sites-sub">{attrs}</div>}
        {row.notes && <div className="sites-sub">{row.notes}</div>}
        {row.provenance?.summary && <div className="sites-sub">{row.provenance.summary}</div>}
      </div>
      <div className="sites-actions">
        {isCandidate ? (
          <>
            <button type="button" disabled={act.isPending} onClick={() => act.mutate('approve')}>
              <Check size={13} aria-hidden /> Approve
            </button>
            <button
              type="button" disabled={act.isPending} onClick={() => act.mutate('reject')}
              title="Kept as rejected, so an assistant does not propose it again"
            >
              <X size={13} aria-hidden /> Reject
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => onEdit(row)}>
              <Pencil size={13} aria-hidden /> Edit
            </button>
            {isRetired ? (
              <button type="button" disabled={act.isPending} onClick={() => act.mutate('reinstate')}>
                <RotateCcw size={13} aria-hidden /> It is back
              </button>
            ) : (
              <button
                type="button" disabled={act.isPending} onClick={() => act.mutate('retire')}
                title="Kept on record — knowing what came out is worth as much as knowing what is in"
              >
                <Archive size={13} aria-hidden /> Removed
              </button>
            )}
          </>
        )}
      </div>
    </li>
  );
}

/**
 * The technology section of a site's detail panel.
 *
 * `tech` and `techSummary` come from the site detail read, so this never
 * re-groups the rows itself — a second grouping is a second answer, and the
 * two would eventually disagree.
 */
export function SiteTechSection({ site, tech, summary, onOpenTab }) {
  const [editing, setEditing] = useState(null);
  const active = tech.filter((t) => t.status === 'active');
  const candidates = tech.filter((t) => t.status === 'candidate');
  const retired = tech.filter((t) => t.status === 'retired');

  const summaryLine = (summary || [])
    .map((s) => `${s.count}${s.exact ? '' : '+'} ${s.label.toLowerCase()}`)
    .join(', ');

  return (
    <section className="sites-tech">
      <h5>
        <Cpu size={14} aria-hidden /> Technology on site
        {active.length > 0 && <span className="sites-muted"> · {summaryLine}</span>}
      </h5>

      {tech.length === 0 && !editing && (
        <p className="accounts-panel-note">
          Nothing recorded yet. No public source states what an agency has deployed at a
          building — this fills in from meetings and from what you enter, so an empty list
          means nobody has told us, not that the site has nothing.
        </p>
      )}

      {candidates.length > 0 && (
        <p className="sites-muted">
          {candidates.length} proposed {candidates.length === 1 ? 'claim' : 'claims'} below are
          not part of this site&apos;s estate until you approve them.
        </p>
      )}

      {tech.length > 0 && (
        <ul className="sites-tech-list">
          {[...candidates, ...active, ...retired].map((t) => (
            <TechRow key={t.siteTechId} row={t} siteId={site.siteId} onEdit={setEditing} />
          ))}
        </ul>
      )}

      {editing ? (
        <TechForm
          siteId={site.siteId}
          row={editing === 'new' ? null : editing}
          onDone={() => setEditing(null)}
          onCancel={() => setEditing(null)}
        />
      ) : (
        <div className="sites-actions">
          <button type="button" className="sites-add-btn" onClick={() => setEditing('new')}>
            <Plus size={14} aria-hidden /> Record technology here
          </button>
          {onOpenTab && (
            /*
             * §12.5: the Tech Map is the LOGICAL estate, this is the PHYSICAL
             * one. The link goes one way on purpose — see the note in the
             * roadmap. Nothing is copied between them automatically.
             */
            <button
              type="button" className="sites-link-btn" onClick={() => onOpenTab('techmap')}
              title="The Tech Map is the account's logical estate. This list is where the boxes physically are."
            >
              <Network size={13} aria-hidden /> Open the Tech Map
              <ExternalLink size={11} aria-hidden />
            </button>
          )}
        </div>
      )}
    </section>
  );
}

/* ─────────────────────── the account-level gap ─────────────────────── */

/**
 * How much of the account we know nothing about.
 *
 * Deliberately renders the GAP as the headline. A rep scanning a short tech
 * list will read it as a small estate unless the panel says otherwise, and
 * "coverage is stated, never implied" is roadmap §10.
 */
export function TechCoverageStrip({ account }) {
  const { data, error } = useQuery({
    queryKey: ['accounts-tech-coverage', account],
    queryFn: () => apiClient.get('/api/sites/tech-coverage', { params: { dept: account } }),
    enabled: !!account,
    staleTime: 5 * 60 * 1000,
  });

  const blanks = useMemo(
    () => (data?.sites || []).filter((s) => s.techCount === 0).slice(0, 5),
    [data],
  );

  if (error || !data || !data.staffedSites) return null;

  return (
    <div className="sites-tech-coverage">
      <div className="sites-tech-coverage-head">
        <Cpu size={14} aria-hidden />
        <strong>
          Technology known at {data.sitesWithTech} of {data.staffedSites} staffed site
          {data.staffedSites === 1 ? '' : 's'}
        </strong>
        {data.coveragePct != null && (
          <span className="sites-muted"> ({data.coveragePct}%)</span>
        )}
      </div>
      <p className="sites-muted">{data.basis}</p>
      {blanks.length > 0 && (
        <p className="sites-muted">
          Biggest blanks:{' '}
          {blanks.map((s) => (
            s.employeeCount ? `${s.siteName} (${s.employeeCount})` : s.siteName
          )).join(' · ')}
          {data.sitesWithoutTech > blanks.length
            && ` and ${data.sitesWithoutTech - blanks.length} more`}
        </p>
      )}
    </div>
  );
}

/* ─────────────────────── the account-level queue ─────────────────────── */

/**
 * Tech claims an assistant filed, across every site on the account.
 *
 * This queue is the reason `submit_site_tech` exists at all: decision #26 held
 * the verb back through S6 precisely because a submission with no reviewer UI
 * lands somewhere nobody looks.
 */
export function TechReviewQueue({ account }) {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['accounts-tech-candidates', account],
    queryFn: () => apiClient.get('/api/sites/tech/candidates', { params: { dept: account } }),
    enabled: !!account,
    staleTime: 60 * 1000,
  });

  const act = useMutation({
    mutationFn: ({ siteTechId, verb }) => apiClient.post(`/api/sites/tech/${siteTechId}/${verb}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts-tech-candidates', account] });
      queryClient.invalidateQueries({ queryKey: ['accounts-sites'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-tech-coverage', account] });
      queryClient.invalidateQueries({ queryKey: ['site-detail'] });
    },
  });

  const rows = data?.candidates || [];
  if (!rows.length) return null;

  return (
    <section className="sites-review">
      <h4>
        <Inbox size={14} aria-hidden /> Technology waiting for review ({rows.length})
      </h4>
      <p className="sites-muted">
        Proposed from public research. None of these count as deployed until you approve
        them — and a claim about what is inside a building is worth reading twice, because
        nothing published will confirm it.
      </p>
      <table className="sites-table">
        <tbody>
          {rows.map((c) => (
            <tr key={c.siteTechId}>
              <td>
                <strong>
                  {siteTechLabel({ role: c.role, quantity: c.quantity, rawLabel: c.rawLabel })}
                </strong>
                <div className="sites-sub">at {c.siteName}{c.city ? `, ${c.city}` : ''}</div>
                {attrLine(c.attributes) && <div className="sites-sub">{attrLine(c.attributes)}</div>}
                {c.basis && <div className="sites-sub">{c.basis}</div>}
                {c.evidenceUrl && (
                  <div className="sites-sub">
                    <a href={c.evidenceUrl} target="_blank" rel="noreferrer noopener">
                      source <ExternalLink size={10} aria-hidden />
                    </a>
                  </div>
                )}
              </td>
              <td className="sites-muted">{c.createdBy}</td>
              <td>
                <div className="sites-actions">
                  <button
                    type="button" disabled={act.isPending}
                    onClick={() => act.mutate({ siteTechId: c.siteTechId, verb: 'approve' })}
                  >
                    <Check size={13} aria-hidden /> Approve
                  </button>
                  <button
                    type="button" disabled={act.isPending}
                    onClick={() => act.mutate({ siteTechId: c.siteTechId, verb: 'reject' })}
                    title="Kept as rejected, so it is not proposed again"
                  >
                    <X size={13} aria-hidden /> Reject
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
